import { BLOCK_CODE_LINE, BLOCK_PARAGRAPH, BLOCK_TABLE } from '@gitbook/doc-core';
import type { Change } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import { isBackspace, isEnter, isShiftEnter } from '../common';

export const plugin: Plugin = {
    onKeyDown,
};

/*
 * On key down when editing
 */
function onKeyDown(event: KeyboardEvent, change: Change): Change | undefined {
    if (isEnter(event) || isShiftEnter(event)) {
        return onEnter(event, change);
    }

    if (isBackspace(event)) {
        return onBackspace(event, change);
    }

    return;
}

function onEnter(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;
    if (value.isExpanded) {
        return;
    }

    const { startBlock, startOffset, endOffset } = value;

    if (!startBlock) {
        return;
    }

    if (startBlock.isVoid) {
        // Enter in a void node is handled by the voidPlugin
        return;
    }

    if (startBlock.type === BLOCK_PARAGRAPH) {
        // Shift+Enter should insert a soft newline in paragraph
        if (isShiftEnter(event)) {
            return change.insertText('\n');
        }
    } else {
        // Pressing enter in an empty block, reset type to paragraph
        const textLength = startBlock.text.length;
        if (startOffset === 0 && textLength === 0) {
            return onBackspace(event, change);
        }

        // Pressing inside the block, split it and insert a paragraph after
        if (endOffset === textLength) {
            event.preventDefault();
            return (
                change
                    .splitBlock()
                    // Slate copies the data on split, so clear them
                    .setBlocks({ type: BLOCK_PARAGRAPH, data: {} })
            );
        }
    }

    return;
}

function onBackspace(event: Event, change: Change): Change | undefined {
    const { value } = change;

    // Long selection -> normal backspace
    if (value.isExpanded) {
        return;
    }

    // Not at start of block (exit as soon as possible for performances)
    if (value.startOffset > 0) {
        return;
    }

    const { startBlock, startText } = value;
    if (!startBlock) {
        return;
    }

    const isStartOfBlock = startBlock.getFirstText() === startText;

    // Not at start of block
    if (!isStartOfBlock) {
        return;
    }

    if (
        startBlock.type === BLOCK_PARAGRAPH ||
        startBlock.type === BLOCK_TABLE ||
        // Ignore code lines, which handle themselves
        startBlock.type === BLOCK_CODE_LINE
    ) {
        return;
    }

    event.preventDefault();

    if (startBlock.isVoid) {
        // Delete the void
        return change.removeNodeByKey(startBlock.key);
    }

    // Convert to a paragraph
    return change.setNodeByKey(startBlock.key, {
        type: BLOCK_PARAGRAPH,
        isVoid: false,
    });
}
